/* eslint-disable @typescript-eslint/no-unnecessary-condition,@typescript-eslint/no-unsafe-member-access */
import {Box, Divider, Flex, Theme} from 'exsportia-components'
import {useFormikContext} from 'formik'
import React, {FC, memo, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'

import {useSubscriptionBooking} from '../../api/subscription-bookings/hooks'
import {useVenue} from '../../api/venue/hooks'
import {CheckoutGateNotice} from '../../components/checkout-gate-notice/checkout-gate-notice'
import {EmptyList} from '../../components/empty-list/empty-list'
import {ExsportiaPayIcon, PaymentMethodsGroup} from '../../components/payment-icons'
import {SubscriptionPreviewCard} from '../../components/subscription-preview-card/subscription-preview-card'
import {GridLayout} from '../../layout/grid-layout/grid-layout'
import {SimpleGridLayoutHeader} from '../../layout/grid-layout/headers/simple-grid-layout-header'
import {useAuthState, useHandleLoginState} from '../../services/auth/hooks'
import {PaymentSubmit, PaymentTypes, usePaymentOptions} from '../../services/payments/hooks/use-payment-options'
import {PaySubscriptionCycleFormType} from '../../services/subscription-bookings/forms/pay-subscription-cycles-form'
import {Button} from '../../ui/components/button/button'
import {RadioButton} from '../../ui/components/radio-button'
import {useLocale} from '../../utils/i18n'
import {UserPaymentsMethods} from '../set-payment-method/components/user-payments-type-modal'
import {PaySubscriptionBookingInstallmentsOptions} from './components/pay-sub-booking-installment-options'


// TODO: move to separated component file
type Props = {
  paymentType: PaymentTypes
  isLoading: boolean
  onClick: () => void
  disabled?: boolean
}
const SubmitButton: FC<Props> = memo(({
  paymentType,
  isLoading,
  onClick,
  disabled = false,
}) => {
  const {l} = useLocale()

  const text = paymentType === PaymentTypes.PAY_ON_LOCATION
    ? l('book')
    : paymentType === PaymentTypes.ONE_TIME
      ? l('continue')
      : l('pay')

  return (
    <Button
      disabled={isLoading || disabled}
      onClick={onClick}
      isLoading={isLoading}
    >
      {text}
    </Button>
  )
})


const Header: FC = memo(() => {
  const {l} = useLocale()
  return <SimpleGridLayoutHeader title={l('pay_next_cycle', {caseAction: 'capitalize'})} />
})

export const SubscriptionBookingUpdatePayNextCyclePage: FC = () => {
  const {l} = useLocale()
  const navigate = useNavigate()
  const {data: venue, isFetching: isVenueLoading} = useVenue()
  const {signUp} = useAuthState()
  const {submitForm, setValues, values, isSubmitting} = useFormikContext<PaySubscriptionCycleFormType>()
  const {data: subscriptionBooking} = useSubscriptionBooking(values.subscriptionBooking?.uid)

  const handleSubmitWithPayment = useCallback(
    (paymentMethodToken: string | null) => {
      setValues({...values, paymentMethodToken, paymentType: PaymentTypes.PAY_NOW})
      submitForm().catch(e => console.log(String(e)))
    },
    [values, setValues, submitForm],
  )
  const handleClickButton: PaymentSubmit = useCallback(({
    paymentType,
    paymentMethodToken
  }) => {
    if (paymentType === PaymentTypes.PAY_NOW) {
      return handleSubmitWithPayment(paymentMethodToken)
    }
    if (paymentType === PaymentTypes.ONE_TIME) {
      setValues({
        ...values,
        paymentType: PaymentTypes.ONE_TIME,
        paymentMethodToken: null
      })
      submitForm().catch(e => console.log(String(e)))
      return
    }
  }, [submitForm, values, handleSubmitWithPayment])
  const {
    paymentType,
    allowedOptions,
    setPaymentType,
    submitWithPayment,
    paymentMethodToken,
    setUserPaymentToken,
    checkoutGate,
    isCheckingCharge,
  } = usePaymentOptions({
    submit: handleClickButton,
    // @ts-ignore
    entity: subscriptionBooking, // TODO: fix type
    entityType: 'subscription-booking',
  })
  const isLoading = isVenueLoading || isSubmitting

  const handleLogOut = useCallback(() => {
    signUp()
  }, [values])

  useHandleLoginState({
    handleLogOut,
    handleLoggedOut: handleLogOut,
  })

  const navigateBackToProfile = useCallback(() => {
    navigate('../')
  }, [])
  const setOneTimePayment = useCallback(() => setPaymentType(PaymentTypes.ONE_TIME), [])

  return (
    <GridLayout header={<Header />}>
      {
        (allowedOptions.length === 0)
          ? (
            <EmptyList
              onClick={navigateBackToProfile}
              text={l('byRequestOnly')}
              buttonText={l('back')}
            />
          )
          : (
            <Box>
              <CheckoutGateNotice gate={checkoutGate} />
              <Box mb='16px'>
                <Divider />
                {
                  (allowedOptions.includes(PaymentTypes.ONE_TIME)) && (
                    <Flex alignItems='center' height={Theme?.spaces?.multiplier * 6}>
                      <RadioButton
                        disabled={isLoading || checkoutGate.isGated}
                        checked={paymentType === PaymentTypes.ONE_TIME}
                        onChange={setOneTimePayment}
                        text={l('exsportiaPay', {caseAction: 'capitalize'})}
                        iconBefore={<ExsportiaPayIcon w={40} h={24} />}
                        iconAfter={<Box ml='auto'><PaymentMethodsGroup w={180} h={32} /></Box>}
                      />
                    </Flex>
                  )
                }
                <Divider />
                {
                  allowedOptions.includes(PaymentTypes.PAY_NOW) && (
                    <>
                      <Box>
                        <UserPaymentsMethods
                          isLoading={isLoading || checkoutGate.isGated}
                          venueId={venue?.uid}
                          paymentMethod={paymentMethodToken}
                          setPaymentMethod={setUserPaymentToken} />
                      </Box>
                    </>
                  )
                }
              </Box>
              <SubmitButton
                paymentType={paymentType}
                isLoading={isLoading || isCheckingCharge}
                onClick={submitWithPayment}
                disabled={checkoutGate.isSelectionBlocked}
              />
            </Box>
          )
      }
      <Box>
        <SubscriptionPreviewCard id={subscriptionBooking?.membershipId} />
        <PaySubscriptionBookingInstallmentsOptions/>
      </Box>
    </GridLayout>
  )
}
